/**
 * FloatingToolbar — Advanced Click-to-Select Toolbar
 *
 * Figma/Canva-quality toolbar with contextual actions per element type.
 * Features: formatting, color, opacity, duplicate, copy/paste style,
 * move up/down, quick border-radius, link editing, and element info.
 * Glass-morphism design with smooth position tracking.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Bold, Italic, Underline, Strikethrough,
  AlignLeft, AlignCenter, AlignRight,
  Image, Trash2, Type, Minus, Plus, Palette,
  Box, Link2, Copy, Clipboard, ArrowUp, ArrowDown,
  Layers, Square, Circle, Eye
} from 'lucide-react';

const TOOLBAR_GAP = 10;

// ── Reusable Button ──────────────────────────────────────────────────
function ToolbarButton({ icon: Icon, title, onClick, isDanger, isActive, badge, disabled }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 38, height: 38, border: 'none', borderRadius: 9,
        position: 'relative',
        background: isActive
          ? 'rgba(99,102,241,0.2)'
          : hovered
            ? (isDanger ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.12)')
            : 'transparent',
        color: disabled
          ? 'rgba(255,255,255,0.2)'
          : isActive
            ? '#a5b4fc'
            : hovered
              ? (isDanger ? '#fca5a5' : '#ffffff')
              : '#cbd5e1',
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: 0,
        transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hovered && !disabled ? 'scale(1.08)' : 'scale(1)',
        opacity: disabled ? 0.4 : 1,
        boxShadow: isActive ? '0 0 0 2px rgba(99,102,241,0.5)' : 'none',
      }}
    >
      <Icon size={15} />
      {badge && (
        <span style={{
          position: 'absolute', top: 2, right: 2,
          width: 7, height: 7, borderRadius: '50%',
          background: '#6366f1',
          boxShadow: '0 0 4px rgba(99,102,241,0.6)',
        }} />
      )}
    </button>
  );
}

// ── Separator ────────────────────────────────────────────────────────
function Separator() {
  return <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.12)', flexShrink: 0, margin: '0 4px' }} />;
}

// ── Mini Color Picker ────────────────────────────────────────────────
function MiniColorButton({ currentColor, onColorChange, title = 'Text Color' }) {
  const inputRef = useRef(null);
  return (
    <div style={{ position: 'relative' }}>
      <button
        title={title}
        onClick={() => inputRef.current?.click()}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 38, height: 38, border: 'none', borderRadius: 9,
          background: 'transparent', cursor: 'pointer', padding: 0,
          position: 'relative', transition: 'all 0.15s ease',
        }}
      >
        <Palette size={15} style={{ color: '#cbd5e1' }} />
        <div style={{
          position: 'absolute', bottom: 5, left: 9, right: 9, height: 3,
          borderRadius: 2,
          background: currentColor || '#ffffff',
        }} />
      </button>
      <input
        ref={inputRef}
        type="color"
        value={currentColor || '#ffffff'}
        onChange={(e) => onColorChange(e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </div>
  );
}

// ── Opacity Slider (inline compact) ──────────────────────────────────
function OpacitySlider({ value, onChange }) {
  const [show, setShow] = useState(false);
  const opacity = value ?? 1;
  const pct = Math.round(opacity * 100);

  return (
    <div style={{ position: 'relative' }}>
      <button
        title={`Opacity: ${pct}%`}
        onClick={() => setShow(!show)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 38, height: 38, border: 'none', borderRadius: 9,
          background: show ? 'rgba(255,255,255,0.12)' : 'transparent',
          cursor: 'pointer', padding: 0, color: '#cbd5e1',
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Eye size={15} style={{ opacity: opacity }} />
      </button>
      {show && (
        <div
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            marginTop: 8, padding: '8px 12px',
            background: 'rgba(10, 15, 28, 0.95)', backdropFilter: 'blur(20px)',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 8,
            whiteSpace: 'nowrap', zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="range"
            min="0" max="1" step="0.05"
            value={opacity}
            onChange={(e) => onChange(parseFloat(e.target.value))}
            style={{
              width: 80, height: 4, cursor: 'pointer',
              accentColor: '#6366f1',
            }}
          />
          <span style={{
            fontSize: 11, fontWeight: 600, color: '#94a3b8',
            fontFamily: 'ui-monospace, monospace', minWidth: 30, textAlign: 'right',
          }}>
            {pct}%
          </span>
        </div>
      )}
    </div>
  );
}

// ── Border Radius Quick Adjuster ─────────────────────────────────────
function RadiusButton({ value, onChange }) {
  const [show, setShow] = useState(false);
  const current = parseInt(value) || 0;

  return (
    <div style={{ position: 'relative' }}>
      <button
        title={`Corner roundness: ${current}px`}
        onClick={() => setShow(!show)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 38, height: 38, border: 'none', borderRadius: 9,
          background: show ? 'rgba(255,255,255,0.12)' : 'transparent',
          cursor: 'pointer', padding: 0, color: '#cbd5e1',
          transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <Square size={15} style={{ borderRadius: Math.min(current / 4, 4) }} />
      </button>
      {show && (
        <div
          style={{
            position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
            marginTop: 8, padding: '8px 12px',
            background: 'rgba(10, 15, 28, 0.95)', backdropFilter: 'blur(20px)',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap', zIndex: 10,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {[0, 4, 8, 12, 16, 24, 9999].map((r) => (
            <button
              key={r}
              onClick={() => { onChange(r === 9999 ? '9999px' : `${r}px`); setShow(false); }}
              title={r === 9999 ? 'Full circle' : `${r}px`}
              style={{
                width: 28, height: 28, border: '1px solid',
                borderColor: current === r ? '#6366f1' : 'rgba(255,255,255,0.1)',
                borderRadius: r === 9999 ? '50%' : Math.min(r, 10),
                background: current === r ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#94a3b8', fontFamily: 'monospace',
                transition: 'all 0.15s',
              }}
            >
              {r === 9999 ? <Circle size={10} /> : r}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline Link Dropdown ─────────────────────────────────────────────
function LinkDropdown({ currentLink, onAction, onClose }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const groups = [
    { group: 'Pages', items: [
      { label: 'Home', value: '/' },
      { label: 'Shop', value: '/shop' },
      { label: 'Categories', value: '/categories' },
      { label: 'Collections', value: '/collections' },
      { label: 'New Arrivals', value: '/new-arrivals' },
      { label: 'Sale', value: '/sale' },
      { label: 'Bestsellers', value: '/bestsellers' },
      { label: 'About', value: '/about' },
      { label: 'Contact', value: '/contact' },
    ]},
    { group: 'Shopping', items: [
      { label: 'Cart', value: '/cart' },
      { label: 'Wishlist', value: '/wishlist' },
      { label: 'Login', value: '/login_page' },
      { label: 'Register', value: '/register' },
      { label: 'Order History', value: '/previous_order' },
      { label: 'Search', value: '/search' },
    ]},
    { group: 'Legal', items: [
      { label: 'Privacy Policy', value: '/privacy_policy' },
      { label: 'Shipping & Returns', value: '/shipping_delivery' },
      { label: 'Terms & Conditions', value: '/terms_conditions' },
      { label: 'FAQ', value: '/faq' },
    ]},
  ];

  const [customUrl, setCustomUrl] = React.useState('');

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'absolute',
        top: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginTop: '8px',
        width: '280px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
        border: '1px solid #e2e8f0',
        zIndex: 1000,
        overflow: 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {/* Current link display */}
      {currentLink && (
        <div style={{
          padding: '8px 12px',
          backgroundColor: '#f0f9ff',
          borderBottom: '1px solid #e2e8f0',
          fontSize: '11px',
          color: '#0369a1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: 8 }}>
            Current: {currentLink}
          </span>
          <button
            onClick={() => { onAction('link-set', ''); onClose(); }}
            style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '11px', fontWeight: 600, flexShrink: 0 }}
          >
            Remove
          </button>
        </div>
      )}

      {/* Quick page links - scrollable list */}
      <div style={{ maxHeight: '240px', overflowY: 'auto', padding: '4px' }}>
        {groups.map(group => (
          <div key={group.group}>
            <div style={{ padding: '6px 8px', fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {group.group}
            </div>
            {group.items.map(item => (
              <button
                key={item.value}
                onClick={() => {
                  onAction('link-set', item.value);
                  onClose();
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '7px 8px',
                  border: 'none',
                  backgroundColor: currentLink === item.value ? '#f0f9ff' : 'transparent',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: currentLink === item.value ? '#0369a1' : '#374151',
                  fontWeight: currentLink === item.value ? 600 : 400,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => { if (currentLink !== item.value) e.currentTarget.style.backgroundColor = '#f8fafc'; }}
                onMouseLeave={(e) => { if (currentLink !== item.value) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ flex: 1 }}>{item.label}</span>
                <span style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'ui-monospace, monospace' }}>{item.value}</span>
                {currentLink === item.value && <span style={{ fontSize: '11px', color: '#0369a1', flexShrink: 0 }}>&#10003;</span>}
              </button>
            ))}
          </div>
        ))}
        {/* Custom URL input (M9 fix) */}
        <div style={{ borderTop: '1px solid #e2e8f0', padding: '8px' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
            Custom URL
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <input
              type="text"
              value={customUrl}
              onChange={(e) => setCustomUrl(e.target.value)}
              placeholder="https://... or /page"
              style={{ flex: 1, padding: '6px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '6px', outline: 'none' }}
              onKeyDown={(e) => { if (e.key === 'Enter' && customUrl.trim()) { onAction('link-set', customUrl.trim()); onClose(); } }}
            />
            <button
              onClick={() => { if (customUrl.trim()) { onAction('link-set', customUrl.trim()); onClose(); } }}
              style={{ padding: '6px 10px', fontSize: '12px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
            >Go</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Toolbar ─────────────────────────────────────────────────────
export default function FloatingToolbar({ selectedElement, onAction, containerRef, copiedStyle }) {
  const toolbarRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0, visible: false });
  const [showLinkDropdown, setShowLinkDropdown] = useState(false);
  const [formatStates, setFormatStates] = useState({ bold: false, italic: false, underline: false, strikethrough: false });

  // Close link dropdown when selection changes
  useEffect(() => {
    setShowLinkDropdown(false);
  }, [selectedElement]);

  // Track bold/italic/underline/strikethrough active state from selection
  useEffect(() => {
    const updateStates = () => {
      try {
        setFormatStates({
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline'),
          strikethrough: document.queryCommandState('strikeThrough'),
        });
      } catch (_) { /* ignore if not supported */ }
    };
    document.addEventListener('selectionchange', updateStates);
    return () => document.removeEventListener('selectionchange', updateStates);
  }, []);

  // ── Positioning logic ──
  const reposition = useCallback(() => {
    if (!selectedElement?.domNode || !containerRef?.current || !toolbarRef.current) {
      setPos(p => ({ ...p, visible: false }));
      return;
    }
    const elRect = selectedElement.domNode.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    const tbRect = toolbarRef.current.getBoundingClientRect();

    // Zoom compensation: if the container has a CSS zoom/transform scale,
    // getBoundingClientRect returns screen-space coords. Detect the effective
    // scale by comparing the container's offsetWidth (layout) to its rect width (visual).
    const containerEl = containerRef.current;
    const zoomScale = containerEl.offsetWidth ? (containerRect.width / containerEl.offsetWidth) : 1;
    const invZoom = zoomScale !== 0 ? (1 / zoomScale) : 1;

    let top = (elRect.top - containerRect.top) * invZoom - tbRect.height * invZoom - TOOLBAR_GAP;
    let left = (elRect.left - containerRect.left) * invZoom + (elRect.width * invZoom - tbRect.width) / 2;

    // Clamp horizontal
    const maxLeft = containerEl.offsetWidth - tbRect.width - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    // Flip below if no room above
    if (top < 8) {
      top = (elRect.bottom - containerRect.top) * invZoom + TOOLBAR_GAP;
    }

    setPos({ top, left, visible: true });
  }, [selectedElement, containerRef]);

  useEffect(() => {
    requestAnimationFrame(reposition);
  }, [reposition, selectedElement]);

  // Reposition on scroll/resize
  useEffect(() => {
    if (!selectedElement) return;
    const container = containerRef?.current;
    const handleScroll = () => requestAnimationFrame(reposition);
    if (container) container.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      if (container) container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [selectedElement, containerRef, reposition]);

  // Click-away to dismiss
  useEffect(() => {
    if (!selectedElement) return;
    const handler = (e) => {
      if (
        toolbarRef.current && !toolbarRef.current.contains(e.target) &&
        selectedElement.domNode && !selectedElement.domNode.contains(e.target)
      ) {
        onAction('dismiss');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedElement, onAction]);

  if (!selectedElement) return null;

  const isText = selectedElement.type === 'text';
  const isImage = selectedElement.type === 'image';
  const isElement = selectedElement.type === 'element';
  const dom = selectedElement.domNode;

  // Show link button ONLY for buttons and anchor elements (not plain text)
  const isLinkable =
    selectedElement.tagName === 'a' ||
    selectedElement.tagName === 'button' ||
    (selectedElement.structure?.events?.onClick?.type === 'navigate') ||
    !!selectedElement.structure?.attributes?.href ||
    (dom && (dom.tagName === 'A' || dom.tagName === 'BUTTON' || dom.closest?.('a')));

  // Detect current link destination for preview badge
  const currentLink =
    selectedElement.structure?.events?.onClick?.target ||
    selectedElement.structure?.events?.onClick?.path ||
    (typeof selectedElement.structure?.events?.click === 'string' && selectedElement.structure.events.click.startsWith('navigate:')
      ? selectedElement.structure.events.click.substring(9)
      : '') ||
    selectedElement.structure?.action?.path ||
    selectedElement.structure?.action?.target ||
    selectedElement.structure?.attributes?.href ||
    selectedElement.structure?.href || '';

  // Get computed values for display
  const computedFontSize = dom ? Math.round(parseFloat(window.getComputedStyle(dom).fontSize) || 16) : 16;
  const computedOpacity = dom ? parseFloat(window.getComputedStyle(dom).opacity) : 1;
  const computedRadius = dom ? window.getComputedStyle(dom).borderRadius : '0px';
  const computedColor = dom ? window.getComputedStyle(dom).color : '';
  const computedBgColor = dom ? window.getComputedStyle(dom).backgroundColor : '';

  // Element size info
  const elWidth = dom ? Math.round(dom.offsetWidth) : 0;
  const elHeight = dom ? Math.round(dom.offsetHeight) : 0;

  return (
    <div
      ref={toolbarRef}
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        opacity: pos.visible ? 1 : 0,
        pointerEvents: pos.visible ? 'auto' : 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 3,
        padding: '6px 10px',
        background: 'rgba(15, 23, 42, 0.92)',
        backdropFilter: 'blur(24px) saturate(1.6)',
        borderRadius: 16,
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 16px 48px rgba(0,0,0,0.4), 0 6px 16px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.04)',
        zIndex: 9999,
        transition: 'opacity 0.15s ease, top 0.15s ease, left 0.15s ease, transform 0.15s ease',
        transform: pos.visible ? 'translateY(0)' : 'translateY(6px)',
        userSelect: 'none',
        flexWrap: 'wrap',
        maxWidth: 'calc(100vw - 40px)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* ── Type indicator + dimensions ── */}
      <div style={{
        display: 'flex', alignItems: 'center', padding: '0 6px',
        color: isText ? '#93c5fd' : isImage ? '#a78bfa' : '#86efac',
        fontSize: '10px', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
        gap: '4px',
      }}>
        {isText ? <Type size={12} /> : isImage ? <Image size={12} /> : <Box size={12} />}
        <span>{isText ? 'Text' : isImage ? 'Image' : 'Element'}</span>
        <span style={{
          fontSize: 9, color: 'rgba(255,255,255,0.25)', fontWeight: 400,
          fontFamily: 'ui-monospace, monospace', marginLeft: 2,
        }}>
          {elWidth}×{elHeight}
        </span>
      </div>
      <Separator />

      {/* ── Text formatting ── */}
      {isText && (
        <>
          <ToolbarButton icon={Bold} title="Bold (Ctrl+B)" onClick={() => onAction('bold')} isActive={formatStates.bold} />
          <ToolbarButton icon={Italic} title="Italic (Ctrl+I)" onClick={() => onAction('italic')} isActive={formatStates.italic} />
          <ToolbarButton icon={Underline} title="Underline (Ctrl+U)" onClick={() => onAction('underline')} isActive={formatStates.underline} />
          <ToolbarButton icon={Strikethrough} title="Strikethrough" onClick={() => onAction('strikethrough')} isActive={formatStates.strikethrough} />
          <Separator />
          <ToolbarButton icon={AlignLeft} title="Align Left" onClick={() => onAction('align-left')} />
          <ToolbarButton icon={AlignCenter} title="Align Center" onClick={() => onAction('align-center')} />
          <ToolbarButton icon={AlignRight} title="Align Right" onClick={() => onAction('align-right')} />
          <Separator />
          {/* Font size controls */}
          <ToolbarButton icon={Minus} title="Decrease font size" onClick={() => onAction('font-size-decrease')} />
          <span style={{
            fontSize: '11px', color: '#94a3b8', fontWeight: 600,
            minWidth: '36px', textAlign: 'center', userSelect: 'none',
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
          }}>
            {computedFontSize}px
          </span>
          <ToolbarButton icon={Plus} title="Increase font size" onClick={() => onAction('font-size-increase')} />
          <Separator />
          <MiniColorButton
            currentColor={dom?.style?.color || computedColor || ''}
            onColorChange={(color) => onAction('text-color', color)}
            title="Text Color"
          />
          <Separator />
        </>
      )}

      {/* ── Image actions ── */}
      {isImage && (
        <>
          <ToolbarButton icon={Image} title="Replace Image" onClick={() => onAction('replace-image')} />
          <RadiusButton
            value={computedRadius}
            onChange={(val) => onAction('border-radius', val)}
          />
          <Separator />
        </>
      )}

      {/* ── Element actions (containers) ── */}
      {isElement && (
        <>
          <MiniColorButton
            currentColor={computedBgColor}
            onColorChange={(color) => onAction('background-color', color)}
            title="Background Color"
          />
          <RadiusButton
            value={computedRadius}
            onChange={(val) => onAction('border-radius', val)}
          />
          <Separator />
        </>
      )}

      {/* ── Inline link dropdown for linkable elements ── */}
      {!isText && isLinkable && (
        <>
          <div style={{ position: 'relative' }}>
            <ToolbarButton
              icon={Link2}
              title="Add/Edit Link"
              onClick={() => setShowLinkDropdown(!showLinkDropdown)}
              isActive={showLinkDropdown}
              badge={!!currentLink}
            />
            {showLinkDropdown && (
              <LinkDropdown
                currentLink={currentLink}
                onAction={onAction}
                onClose={() => setShowLinkDropdown(false)}
              />
            )}
          </div>
          {/* Compact current-link badge (no popup, just info) */}
          {currentLink && (
            <div
              onClick={() => setShowLinkDropdown(!showLinkDropdown)}
              title={'Link: ' + currentLink}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '2px 8px',
                backgroundColor: 'rgba(59,130,246,0.15)',
                borderRadius: '6px',
                fontSize: '11px',
                color: '#93c5fd',
                maxWidth: '140px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                transition: 'background 0.15s ease',
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentLink}</span>
            </div>
          )}
          <Separator />
        </>
      )}

      {/* ── Link button for text elements that are buttons/anchors, OR text elements with existing links ── */}
      {isText && (currentLink || isLinkable) && (
        <>
          <div style={{ position: 'relative' }}>
            <ToolbarButton
              icon={Link2}
              title="Edit Link"
              onClick={() => setShowLinkDropdown(!showLinkDropdown)}
              isActive={showLinkDropdown}
              badge={true}
            />
            {showLinkDropdown && (
              <LinkDropdown
                currentLink={currentLink}
                onAction={onAction}
                onClose={() => setShowLinkDropdown(false)}
              />
            )}
          </div>
          <div
            onClick={() => setShowLinkDropdown(!showLinkDropdown)}
            title={'Link: ' + currentLink}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '2px 8px',
              backgroundColor: 'rgba(59,130,246,0.15)',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#93c5fd',
              maxWidth: '140px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
            }}
          >
            <Link2 size={10} style={{ flexShrink: 0 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{currentLink}</span>
          </div>
          <Separator />
        </>
      )}

      {/* ── Universal actions ── */}
      <OpacitySlider
        value={computedOpacity}
        onChange={(val) => onAction('opacity', val)}
      />
      <Separator />

      {/* Copy / Paste style */}
      <ToolbarButton
        icon={Copy}
        title="Copy this element's look"
        onClick={() => onAction('copy-style')}
      />
      <ToolbarButton
        icon={Clipboard}
        title="Apply copied look here"
        onClick={() => onAction('paste-style')}
        badge={!!copiedStyle}
        disabled={!copiedStyle}
      />
      <Separator />

      {/* Duplicate */}
      <ToolbarButton icon={Layers} title="Make a copy (Ctrl+D)" onClick={() => onAction('duplicate')} />

      {/* Move up/down */}
      <ToolbarButton icon={ArrowUp} title="Move Up" onClick={() => onAction('move-up')} />
      <ToolbarButton icon={ArrowDown} title="Move Down" onClick={() => onAction('move-down')} />
      <Separator />

      {/* Delete */}
      <ToolbarButton icon={Trash2} title="Delete (Del)" onClick={() => onAction('delete')} isDanger />
    </div>
  );
}
